/**
 * What a person sees on a "What's new" note (mockup B2): a WHAT'S NEW badge,
 * the title, who sent it, one picture per line, and a big Got it. Nothing to
 * do but read — no Done, no reply box. The review screen renders this same
 * component as its preview, so Corbin sees exactly what staff see.
 */
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { fromLine, type NoteStep } from "@/lib/whats-new";
import { NotePic } from "./pics";

export function NoteView(props: {
    title: string;
    steps: NoteStep[];
    senderName: string | null;
    sentAt: string | null;
    gotIt?: boolean;
    busy?: boolean;
    /** Missing = preview (the button shows but does nothing). */
    onGotIt?: () => void;
    compact?: boolean;
}) {
    const { compact } = props;
    return (
        <div className="grid gap-4" data-testid="whats-new-note">
            <div>
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                    What&apos;s new
                </span>
                <h2 className={cn("mt-2 font-extrabold leading-tight", compact ? "text-xl" : "text-2xl sm:text-3xl")}>
                    {props.title || "Title"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    {fromLine({ senderName: props.senderName, sentAt: props.sentAt })}
                </p>
            </div>
            <ol
                className={cn(
                    "m-0 grid list-none gap-4 p-0",
                    !compact && props.steps.length >= 2 && "sm:grid-cols-2",
                    !compact && props.steps.length >= 3 && "lg:grid-cols-3",
                )}
            >
                {props.steps.map((s, i) => (
                    <li
                        key={i}
                        className={cn(
                            compact
                                ? "grid grid-cols-[64px_1fr] items-center gap-3"
                                : "grid content-start gap-2 text-center",
                        )}
                    >
                        <NotePic
                            pic={s.pic}
                            className={compact ? "h-12 w-16" : "aspect-[16/7] w-full sm:aspect-[4/3]"}
                        />
                        <p className={cn("m-0 font-bold leading-snug", compact ? "text-base" : "text-lg")}>{s.line}</p>
                    </li>
                ))}
            </ol>
            {props.gotIt ? (
                <p className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-50 p-4 text-lg font-bold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200">
                    <Check className="h-6 w-6" aria-hidden /> You tapped Got it
                </p>
            ) : (
                <button
                    type="button"
                    onClick={props.onGotIt}
                    disabled={props.busy}
                    className={cn(
                        "flex w-full items-center justify-center gap-2 rounded-2xl bg-primary font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60",
                        compact ? "min-h-[48px] text-base" : "min-h-[72px] text-2xl",
                    )}
                    data-testid="whats-new-got-it"
                >
                    <Check className={compact ? "h-5 w-5" : "h-7 w-7"} aria-hidden /> Got it
                </button>
            )}
        </div>
    );
}
